import { createRootRoute, Outlet, Link, type ErrorComponentProps } from '@tanstack/react-router'
import { Navbar } from '@/components/layout/Navbar'
import { Footer } from '@/components/layout/Footer'
import { buttonVariants } from '@/components/ui/Button'
import { Container } from '@/components/ui/Container'
import { AlertCircle, FileQuestion, RotateCcw, Home } from 'lucide-react'

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RootErrorComponent,
  notFoundComponent: RootNotFoundComponent,
})

function RootLayout() {
  return (
    <div className="flex min-h-screen flex-col bg-surface-0 text-fg transition-colors duration-200 overflow-x-clip">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 z-50 px-4 py-2 bg-btn-primary text-btn-primary-fg rounded-sm text-sm"
      >
        Skip to content
      </a>
      <Navbar />
      <main id="main-content" className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  )
}

function RootNotFoundComponent() {
  return (
    <div className="py-20 sm:py-28 md:py-36">
      <Container className="max-w-2xl text-center flex flex-col items-center">
        <div className="w-12 h-12 rounded-sm bg-surface-2 border border-border flex items-center justify-center text-fg-subtle mb-6">
          <FileQuestion size={24} />
        </div>
        <span className="font-mono text-xs font-semibold px-2.5 py-1 rounded-xs bg-surface-2 text-fg-subtle border border-border-subtle mb-4">
          404 — NOT FOUND
        </span>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-fg text-balance">
          Page Not Found
        </h1>
        <p className="mt-4 text-sm sm:text-base text-fg-subtle leading-relaxed max-w-md">
          The requested system endpoint or page does not exist or has been relocated.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
          <Link
            to="/"
            className={buttonVariants({ variant: 'primary', size: 'md' })}
          >
            <Home size={16} />
            <span>Return to Home</span>
          </Link>
          <Link
            to="/contact"
            className={buttonVariants({ variant: 'outline', size: 'md' })}
          >
            Contact Engineering
          </Link>
        </div>
      </Container>
    </div>
  )
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const errorMessage =
    error instanceof Error ? error.message : 'An unexpected exception halted execution.'

  return (
    <div className="py-20 sm:py-28 md:py-36" role="alert">
      <Container className="max-w-2xl text-center flex flex-col items-center">
        <div className="w-12 h-12 rounded-sm bg-error-bg border border-error/30 flex items-center justify-center text-error mb-6">
          <AlertCircle size={24} />
        </div>
        <span className="font-mono text-xs font-semibold px-2.5 py-1 rounded-xs bg-error-bg text-error border border-error/30 mb-4">
          SYSTEM FAULT DETECTED
        </span>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-fg text-balance">
          Application Error
        </h1>
        <p className="mt-4 text-sm sm:text-base text-fg-subtle leading-relaxed max-w-md">
          A client-side runtime exception occurred. The error details have been isolated below.
        </p>
        <div className="mt-6 w-full text-left bg-surface-1 border border-border p-4 rounded-sm font-mono text-xs text-error overflow-x-auto max-h-40">
          <code>{errorMessage}</code>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
          {reset && (
            <button
              onClick={() => reset()}
              className={buttonVariants({ variant: 'primary', size: 'md' })}
            >
              <RotateCcw size={16} />
              <span>Retry</span>
            </button>
          )}
          <Link
            to="/"
            className={buttonVariants({ variant: 'outline', size: 'md' })}
          >
            <Home size={16} />
            <span>Return to Home</span>
          </Link>
        </div>
      </Container>
    </div>
  )
}
